import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Extracted, IVersionedComponentProps } from './types';
import {
  Dropdown,
  FlexGrid,
  getClassNameFactory,
  getRehydratableName,
  Rank,
  Typography,
} from '@dcl/ui';
import Heading, { types } from '@dcl/ui/dist/elements/Heading';
import { TypographyTags } from '@dcl/ui/dist/elements/Typography';
import ReactHtmlParser from 'react-html-parser';
import './VersionDropdown.css';
import { propsToDataAttributes } from '@tr-digital/react-rehydrate';

export const VersionDropdown: React.FC<IVersionedComponentProps> = (props) => {
  const { versionDescription, versionHeading, versionList } = props;
  const dropdownRef = useRef<Dropdown>();
  const [selectedValue, setSelectedValue] = useState<string>('default');
  const [isComponentMounted, setIsComponentMounted] = useState(false);

  const updatedList = useMemo(() => {
    const defaultItem = { title: 'Current Version', link: 'default' };
    return [defaultItem, ...versionList];
  }, [versionList]);

  // Helper function to update page title
  const updatePageTitle = (titleHtml: string) => {
    const pageH1 =
      document.querySelector('h1.title') ||
      document.querySelector('.topic h1') ||
      document.querySelector('h1:not([class*="VersionDropdown"])');

    if (pageH1 && titleHtml && document.contains(pageH1)) {
      pageH1.innerHTML = titleHtml;
    }
  };

  // Helper function to remove sibling elements
  const removeSiblingElements = (
    versionDropdownParent: HTMLElement,
    grandParent: HTMLElement
  ) => {
    let nextSibling = versionDropdownParent.nextElementSibling;

    while (nextSibling) {
      const elementToRemove = nextSibling;
      nextSibling = nextSibling.nextElementSibling;

      // Using optional chaining and proper conditional logic
      if (
        elementToRemove?.parentNode === grandParent &&
        document.contains(elementToRemove) &&
        grandParent.contains(elementToRemove)
      ) {
        grandParent.removeChild(elementToRemove);
      }
    }
  };

  // Helper function to append description content
  const appendDescriptionContent = (
    grandParent: HTMLElement,
    descriptionHtml: string
  ) => {
    if (descriptionHtml && document.contains(grandParent)) {
      const descriptionContainer = document.createElement('div');
      descriptionContainer.innerHTML = descriptionHtml;
      grandParent.appendChild(descriptionContainer);
    }
  };

  // Helper function to update page description
  const updatePageDescription = (
    componentRoot: Element,
    descriptionHtml: string
  ) => {
    // Using optional chaining instead of multiple && conditions
    if (!componentRoot?.parentNode || !document.contains(componentRoot)) {
      return;
    }

    const versionDropdownParent = componentRoot as HTMLElement;
    const grandParent = componentRoot.parentNode as HTMLElement;

    // Use requestAnimationFrame to ensure DOM is stable before manipulation
    requestAnimationFrame(() => {
      try {
        removeSiblingElements(versionDropdownParent, grandParent);
        appendDescriptionContent(grandParent, descriptionHtml);
      } catch (domError) {
        console.warn(
          'DOM manipulation failed, continuing without content update:',
          domError
        );
      }
    });
  };

  // Helper function to update page content
  const updatePageContent = (titleHtml: string, descriptionHtml: string) => {
    const componentRoot = document.querySelector(`.${getClassName()}`);
    if (!componentRoot) return;

    updatePageTitle(titleHtml);
    updatePageDescription(componentRoot, descriptionHtml);
  };

  // Main function with reduced cognitive complexity
  const handleValueChange = async (value: string) => {
    if (value === 'default') {
      window.location.reload();
      return;
    }

    try {
      let finalUrl = value;
      if (!finalUrl.endsWith('hscontentexporter.json')) {
        finalUrl = `${finalUrl}.hscontentexporter.json`;
      }
      const response = await fetch(finalUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.error('API call failed:', response.statusText);
        return;
      }

      const data = await response.json();
      const { titleHtml, descriptionHtml } = extractContent(
        data.topicdata || ''
      );

      updatePageContent(titleHtml, descriptionHtml);
    } catch (error) {
      console.error('Error calling API:', error);
    }
  };

  const extractContent = (html: string): Extracted => {
    if (!html) return { titleHtml: '', descriptionHtml: '' };

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const h1 = doc.querySelector('h1');

    if (!h1) {
      return {
        titleHtml: '',
        descriptionHtml: doc.body?.innerHTML?.trim() ?? '',
      };
    }

    const titleHtml = h1.innerHTML;

    // Get all elements after h1
    const elementsAfterH1: string[] = [];
    let nextSibling = h1.nextElementSibling;

    while (nextSibling) {
      elementsAfterH1.push(nextSibling.outerHTML);
      nextSibling = nextSibling.nextElementSibling;
    }

    // Remove the first element if it's a div containing an anchor with aria-label="checklists opens in a new tab"
    if (elementsAfterH1.length > 0) {
      const firstElementDoc = parser.parseFromString(
        elementsAfterH1[0],
        'text/html'
      );
      const firstElement = firstElementDoc.body?.firstElementChild;

      if (firstElement?.tagName?.toLowerCase() === 'div') {
        const anchor = firstElement.querySelector(
          'a[aria-label="checklists opens in a new tab"]'
        );
        if (anchor) {
          elementsAfterH1.shift(); // Remove the first element
        }
      }
    }

    const descriptionHtml = elementsAfterH1.join('');

    return { titleHtml, descriptionHtml };
  };

  useEffect(() => {
    if (updatedList && updatedList.length > 0) {
      const defaultItem = updatedList.find((item) => item.link === 'default');
      if (defaultItem) {
        setSelectedValue(defaultItem.link);

        // Only try to set dropdown state after component is fully mounted
        if (isComponentMounted && dropdownRef.current) {
          // Use setTimeout to ensure this happens after render
          setTimeout(() => {
            try {
              dropdownRef.current?.setState({ value: defaultItem.link });
            } catch (error) {
              console.warn('Failed to set dropdown state:', error);
            }
          }, 0);
        }
      }
    }
  }, [updatedList, isComponentMounted]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      try {
        repositionComponent();
      } catch (error) {
        console.error('Error in VersionDropdown DOM manipulation:', error);
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper function to check if component needs repositioning
  const shouldRepositionComponent = (
    componentRoot: Element,
    pageH1: Element
  ) => {
    const nextElement = pageH1?.nextElementSibling;

    return (
      nextElement !== componentRoot &&
      componentRoot.parentNode &&
      pageH1.parentNode &&
      !pageH1.parentNode.contains(componentRoot)
    );
  };

  // Helper function to verify DOM nodes are valid
  const areNodesValid = (componentRoot: Element, pageH1: Element) => {
    return (
      document.contains(componentRoot) &&
      document.contains(pageH1) &&
      pageH1.parentNode
    );
  };

  // Helper function to remove hide class
  const removeHideClass = (componentRoot: Element) => {
    componentRoot?.classList.remove('tr-VersionDropdown--hide');
  };

  // Main repositioning logic
  const repositionComponent = () => {
    const componentRoot = document.querySelector(`.${getClassName()}`);
    if (!componentRoot) return;

    const pageH1 =
      document.querySelector('h1.title') ||
      document.querySelector('.topic h1') ||
      document.querySelector('h1:not([class*="VersionDropdown"])');

    if (!pageH1?.parentNode) return;

    if (!shouldRepositionComponent(componentRoot, pageH1)) return;

    try {
      if (areNodesValid(componentRoot, pageH1)) {
        pageH1.parentNode.insertBefore(componentRoot, pageH1.nextSibling);
        removeHideClass(componentRoot);
      }
    } catch (error) {
      console.warn('Failed to insert component:', error);
    }
  };

  // Effect to track component mount state
  useEffect(() => {
    setIsComponentMounted(true);
    return () => setIsComponentMounted(false);
  }, []);

  const getClassName = getClassNameFactory(VersionDropdown.displayName);
  return (
    <div
      className={getClassName({ modifiers: 'hide' })}
      data-testid="VersionDropdown"
      data-rehydratable={getRehydratableName(VersionDropdown.displayName)}
      {...propsToDataAttributes(props)}
    >
      {updatedList.length > 1 && (
        <FlexGrid fullWidth>
          <FlexGrid.Row>
            <FlexGrid.Col xs={12} md={3} lg={3}>
              <Heading
                rank={Rank.Second}
                className={getClassName({ descendantName: 'title' })}
                type={types.Large}
              >
                {versionHeading}
              </Heading>
              <Typography
                tag={TypographyTags.P}
                className={getClassName({ descendantName: 'label' })}
              >
                {ReactHtmlParser(versionDescription)}
              </Typography>

              <Dropdown
                ref={(el) => (dropdownRef.current = el!)}
                maxVisibleItems={5}
                block={true}
                items={updatedList.map((item) => ({
                  label: item.title,
                  value: item.link,
                }))}
                placeholder=""
                onValueChanged={(value) => handleValueChange(value as string)}
                value={selectedValue}
              />
            </FlexGrid.Col>
          </FlexGrid.Row>
        </FlexGrid>
      )}
    </div>
  );
};

VersionDropdown.displayName = 'VersionDropdown';
